/**
 * Datum en tijd kiezen voor een Mass Message (Amsterdamse tijd): één gedeelde
 * kiezer voor Create, Drafts (bewerken en goedkeuren) en Agenda (bewerken).
 *
 * 04-10-2026 (Jordy): "alleen de keuzelijst (rechts), het typveld (links)
 * mag weg." De keuzelijst is de enige bron van de tijd. Hij toont de
 * kwartieren plus de huidige waarde als die er niet tussen zit (bijvoorbeeld
 * 20:35 na "Now", of een concept op 21:05), zodat wat de knoppen zetten altijd
 * te zien is en de waarde nooit stilletjes naar een andere tijd springt.
 * "Now" rondt naar boven af op de volgende 5 minuten; "+15 min", "+30 min" en
 * "+1 hour" schuiven het getoonde moment op (zie lib/tijdkeuze.ts).
 *
 * 07-10-2026 (Jordy): met een tijdzone (`zone`, standaard Amsterdam). Datum
 * en tijd zijn die van de zone; de knoppen rekenen in die zone. Bij een andere
 * zone dan Amsterdam staat eronder een bevestigingsregel met beide tijden
 * ("22:50 Asia/Manila = 16:50 Amsterdam").
 */
import { Field } from './Modal'
import { kwartierTijden, plusMinuten, standaardMoment, tijdKiesbaar, type DatumTijd } from '../lib/tijdkeuze'
import { AMSTERDAM, bevestigingsRegel, nlDagLabel, zoneLabel } from '../lib/amsterdam'
import { plusDagen, vandaagAmsterdam } from '../lib/agenda'

const KWARTIEREN = kwartierTijden()
const SNELKNOPPEN = [
  { label: '+15 min', minuten: 15 },
  { label: '+30 min', minuten: 30 },
  { label: '+1 hour', minuten: 60 },
]
export const TIJD_HINT = 'Pick a time from the list or use the buttons.'

/**
 * 16-10-2026 (Jordy): de datum als keuzelijst in DD-MM-YYYY ("Thu 22-10-2026"),
 * want het browserveld toonde 10/22/2026. Waarde blijft YYYY-MM-DD. Van 7 dagen
 * terug (een concept mag in het verleden liggen) tot 90 vooruit, plus de huidige
 * waarde als die erbuiten valt.
 */
export function datumOpties(datum: string, zone: string, nu: number = Date.now()): string[] {
  const vandaag = vandaagAmsterdam(nu, zone)
  const uit: string[] = []
  for (let i = -7; i <= 90; i++) uit.push(plusDagen(vandaag, i))
  if (datum && !uit.includes(datum)) uit.push(datum)
  return uit.sort()
}

/** De opties van de keuzelijst: alle kwartieren, plus `tijd` als die er niet tussen zit. */
export function tijdOpties(tijd: string): string[] {
  if (!tijd || KWARTIEREN.includes(tijd)) return KWARTIEREN
  return [...KWARTIEREN, tijd].sort()
}

export function TijdKiezer({
  datum, tijd, onChange, label, naam = '', alleenToekomst = true, hint = TIJD_HINT, zone = AMSTERDAM,
}: {
  datum: string
  tijd: string
  onChange: (m: DatumTijd) => void
  /** Standaard "Date and time (<zone>)". */
  label?: string
  /** IANA-tijdzone waarin datum en tijd gelezen worden (standaard Amsterdam). */
  zone?: string
  /** Voor de schermlezer en de tests: "Slot 1" geeft "Slot 1 date" en "Slot 1 time". */
  naam?: string
  /** Tijden in het verleden grijs (plannen); uit voor een concept. */
  alleenToekomst?: boolean
  hint?: string
}) {
  const voor = naam ? `${naam} ` : ''
  const regel = bevestigingsRegel(datum, tijd, zone)
  return (
    <Field label={label ?? `Date and time (${zoneLabel(zone)})`} hint={hint}>
      <div className="row massa-tijd" style={{ gap: 8 }}>
        <select className="inp" value={datum} aria-label={`${voor}date`.trim()}
                onChange={(e) => onChange({ datum: e.target.value, tijd })}>
          {!datum ? <option value="">Pick a date…</option> : null}
          {datumOpties(datum, zone).map((d) => <option key={d} value={d}>{nlDagLabel(d)}</option>)}
        </select>
        <select className="inp" aria-label={`${voor}time`.trim()} value={tijd}
                onChange={(e) => onChange({ datum, tijd: e.target.value })}>
          {!tijd ? <option value="">Pick a time…</option> : null}
          {tijdOpties(tijd).map((t) => (
            <option key={t} value={t}
                    disabled={alleenToekomst && t !== tijd && !tijdKiesbaar(datum, t, undefined, zone)}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <div className="row massa-snel" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
        <button className="btn ghost sm" type="button" title="Now, rounded up to the next 5 minutes"
                onClick={() => onChange(standaardMoment(undefined, zone))}>
          Now
        </button>
        {SNELKNOPPEN.map((k) => (
          <button key={k.label} className="btn ghost sm" type="button"
                  title="Moves the time shown; if it is empty or already past, counts from now."
                  onClick={() => onChange(plusMinuten(datum, tijd, k.minuten, undefined, zone))}>
            {k.label}
          </button>
        ))}
      </div>
      {regel ? (
        <div className="mut2 mono" style={{ fontSize: 12, marginTop: 6 }} data-testid="time-zone-check">
          {regel}
        </div>
      ) : null}
    </Field>
  )
}
